import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Checkbox, Col, Modal, Row, Tooltip, message } from "antd";
import { useDispatch } from "react-redux";
import {
  Mail,
  Phone,
  Building2,
  Briefcase,
  DollarSign,
  Trash2,
  CheckCircle2,
  XCircle,
} from "lucide-react";

import "../../assets/css/kanban-view.css";
import CopyText from "../web2lead/CopyText";
import noData from "../../assets/images/lead-management/no-data.svg";
import { ReactComponent as WhatsAppIcon } from "../../assets/svg/sidebar/whatsapp.svg";
import UpdateLeadDetails from "./UpdateLeadDetails";
import MarkAsLostModal from "./MarkAsLost";
import { selectedRow } from "../../actionCreator/leadListingActionCreater";
import TagLeads from "./TagLeads";

const customStatus = [
  {
    key: 0,
    title: "Open",
    class: "open",
  },
  {
    key: 1,
    title: "Contacted",
    class: "contacted",
  },
  {
    key: 2,
    title: "Converted",
    class: "converted",
  },
  {
    key: 3,
    title: "Unqualified",
    class: "unqualified",
  },
];

const statusStyles = {
  open: {
    bg: "tw-bg-indigo-50/50",
    border: "tw-border-indigo-100/50",
    text: "tw-text-indigo-600",
    badgeBg: "tw-bg-indigo-600/90",
  },
  contacted: {
    bg: "tw-bg-sky-50/50",
    border: "tw-border-sky-100/50",
    text: "tw-text-sky-600",
    badgeBg: "tw-bg-sky-600/90",
  },
  converted: {
    bg: "tw-bg-emerald-50/50",
    border: "tw-border-emerald-100/50",
    text: "tw-text-emerald-600",
    badgeBg: "tw-bg-emerald-600/90",
  },
  unqualified: {
    bg: "tw-bg-rose-50/50",
    border: "tw-border-rose-100/50",
    text: "tw-text-rose-600",
    badgeBg: "tw-bg-rose-600/90",
  },
};

const KanbanView = ({
  dragStartEvent,
  dropEvent,
  localExecutiveEmployeeList,
  draggableLead,
  updateLead,
  showTagModal,
  setShowTagModal,
  selectedRecords,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [status, setStatus] = useState();
  const [type, setType] = useState();
  const [isConfirm, setConfirm] = useState(false);
  const [isDragged, setDragged] = useState(false);
  const kanbanView = localExecutiveEmployeeList?.map((d) => d?.executiveData);

  const handleCancel = () => {
    setConfirm(false);
    setStatus();
    setDragged(false);
  };

  return (
    <div className="kanban-view tw-w-full">
      <Row className="kanban-container position-relative tw-w-full tw-mx-0">
        {kanbanView &&
          kanbanView
            ?.filter((d) =>
              [
                "New Lead",
                "Sales Qualified",
                "Opportunity ",
                "Un-Qualified",
              ].includes(d?.statusText),
            )
            .map((data, index) => {
              const customItem = customStatus[index];
              const styles = statusStyles[customItem.class];
              let totalAmount = data?.leadsList?.reduce(
                (sum, d) => sum + (parseInt(d?.oppurtunityAmount) || 0),
                0,
              );
              return (
                <Col key={index} xs={6} className="tw-px-1 tw-pb-4">
                  {/* Status header card with soft background colors */}
                  <div
                    className={`tw-flex tw-items-center tw-justify-between tw-p-3.5 tw-rounded-xl tw-border tw-border-solid ${styles.border} ${styles.bg} tw-mb-4 tw-shadow-sm`}
                  >
                    <div className="tw-flex tw-items-center tw-gap-1.5 tw-min-w-0">
                      <span
                        className={`tw-text-xs tw-font-bold tw-uppercase tw-tracking-wider tw-truncate ${styles.text}`}
                      >
                        {customItem?.title}
                      </span>
                      <span className="tw-text-xs tw-font-bold tw-text-slate-700 tw-bg-white/80 tw-px-1.5 tw-py-0.5 tw-rounded-md tw-shadow-sm tw-shrink-0">
                        &#x20b9;{totalAmount}
                      </span>
                    </div>
                    <div
                      className={`tw-text-[11px] tw-font-bold tw-text-white ${styles.badgeBg} tw-px-2 tw-py-0.5 tw-rounded-full tw-shadow-sm tw-shrink-0`}
                    >
                      {data?.totalRecords}
                    </div>
                  </div>

                  {/* Clean lane status container */}
                  <div
                    className="status-container tw-bg-transparent tw-flex tw-flex-col tw-gap-3.5 tw-h-[calc(100vh-230px)] tw-overflow-y-auto custom-scrollbar"
                    onDragOver={(e) => {
                      e.preventDefault();
                    }}
                    onDragEnter={(e) => {
                      e.preventDefault(); // Prevent scrolling
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      if (customItem?.title === "Unqualified") {
                        setType("Un-Qualified");
                      } else {
                        dropEvent(data);
                      }
                      setDragged(false);
                    }}
                  >
                    {data?.leadsList?.length ? (
                      data?.leadsList?.map((lead, i) => (
                        <div
                          key={i}
                          id="cd3"
                          draggable="true"
                          onDragStart={(e) => {
                            setDragged(true);
                            dragStartEvent(e, lead, data);
                          }}
                          className="lead-card draggable tw-bg-white tw-p-4 tw-rounded-xl tw-border tw-border-solid tw-border-slate-200/70 hover:tw-border-theme/35 tw-shadow-sm hover:tw-shadow-md tw-transition-all tw-duration-200 tw-flex tw-gap-2.5 tw-items-start"
                        >
                          <Checkbox
                            className="tw-mt-1 tw-shrink-0"
                            checked={selectedRecords?.find(
                              (d) => d?.id === lead?.id,
                            )}
                            onClick={(val) => {
                              if (val?.target?.checked) {
                                dispatch(
                                  selectedRow([...selectedRecords, lead]),
                                );
                              } else {
                                dispatch(
                                  selectedRow([
                                    ...selectedRecords?.filter(
                                      (d) => d?.id !== lead?.id,
                                    ),
                                  ]),
                                );
                              }
                            }}
                          />
                          <div className="tw-flex tw-flex-col tw-gap-1.5 tw-flex-1 tw-min-w-0">
                            <div className="tw-flex tw-items-center tw-justify-between tw-gap-1.5 tw-w-full tw-min-w-0">
                              <span
                                className="tw-text-[13px] tw-font-bold tw-text-slate-800 hover:tw-text-theme tw-cursor-pointer tw-transition-colors tw-truncate tw-flex-1 tw-min-w-0"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  navigate(`/lead-details/${lead?.id}`);
                                }}
                              >
                                {lead?.fullname
                                  ? lead?.fullname
                                  : (lead?.firstname || "") +
                                    " " +
                                    (lead?.latname || "")}
                              </span>

                              {(lead?.allowedActions &&
                              lead?.allowedActions?.length > 0
                                ? lead?.allowedActions?.includes("EDIT")
                                : data?.allowedActions &&
                                    data?.allowedActions?.length > 0
                                  ? data?.allowedActions?.includes("EDIT")
                                  : true) && (
                                <div
                                  className="tw-shrink-0 tw-z-10"
                                  onMouseDown={(e) => e.stopPropagation()}
                                  onTouchStart={(e) => e.stopPropagation()}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                  }}
                                >
                                  <UpdateLeadDetails lead={lead} />
                                </div>
                              )}
                            </div>
                            {lead?.title && (
                              <div className="tw-flex tw-items-center tw-gap-1.5 tw-text-[11.5px] tw-text-slate-500">
                                <Briefcase size={13} className="tw-text-slate-400 tw-shrink-0" />
                                <span className="tw-truncate tw-font-medium">
                                  {lead?.title}
                                </span>
                              </div>
                            )}
                            {lead?.companyName && (
                              <div className="tw-flex tw-items-center tw-gap-1.5 tw-text-[11.5px] tw-text-slate-400">
                                <Building2 size={13} className="tw-text-slate-400 tw-shrink-0" />
                                <span className="tw-truncate tw-font-medium">
                                  {lead?.companyName}
                                </span>
                              </div>
                            )}

                            {/* Action row */}
                            <div className="tw-flex tw-items-center tw-justify-between tw-mt-1.5 tw-pt-2.5 tw-border-t tw-border-b-0 tw-border-r-0 tw-border-l-0 tw-border-solid tw-border-slate-100/60 tw-w-full">
                              <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-[11px] tw-font-bold tw-text-slate-700 tw-bg-slate-50 tw-border tw-border-solid tw-border-slate-100/80 tw-px-2 tw-py-0.5 tw-rounded-md">
                                <DollarSign size={12} className="tw-text-slate-500 tw-mr-0.5" />
                                &#x20b9;{lead?.oppurtunityAmount || 0}
                              </span>

                              <div className="tw-flex tw-items-center tw-gap-1.5">
                                {(lead?.mobile || lead?.phoneNo) && (
                                  <div className="tw-flex tw-items-center tw-gap-1">
                                    <CopyText
                                      content={
                                        <Tooltip title="Copy Mobile Number">
                                          <button
                                            type="button"
                                            className="tw-flex tw-items-center tw-justify-center tw-size-6 tw-rounded-full tw-bg-slate-50 hover:tw-bg-slate-100 tw-text-slate-450 hover:tw-text-theme tw-cursor-pointer tw-transition-colors tw-border-none tw-outline-none"
                                          >
                                            <Phone size={12} className="tw-text-slate-400" />
                                          </button>
                                        </Tooltip>
                                      }
                                      text={lead?.mobile || lead?.phoneNo}
                                    />
                                    <Tooltip title="Call Mobile Number">
                                      <a
                                        href={`tel:${lead?.mobile || lead?.phoneNo}`}
                                        className="tw-flex tw-items-center tw-justify-center tw-size-6 tw-rounded-full tw-bg-slate-50 hover:tw-bg-slate-100 tw-text-slate-450 hover:tw-text-theme tw-transition-colors"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <Phone size={12} />
                                      </a>
                                    </Tooltip>
                                  </div>
                                )}
                                {lead?.emailId && (
                                  <div className="tw-flex tw-items-center tw-gap-1">
                                    <CopyText
                                      content={
                                        <Tooltip title="Copy Email Address">
                                          <button
                                            type="button"
                                            className="tw-flex tw-items-center tw-justify-center tw-size-6 tw-rounded-full tw-bg-slate-50 hover:tw-bg-slate-100 tw-text-slate-450 hover:tw-text-theme tw-cursor-pointer tw-transition-colors tw-border-none tw-outline-none"
                                          >
                                            <Mail size={12} />
                                          </button>
                                        </Tooltip>
                                      }
                                      text={lead?.emailId}
                                    />
                                    <Tooltip title="Chat on WhatsApp">
                                      <button
                                        type="button"
                                        className="tw-flex tw-items-center tw-justify-center tw-size-6 tw-rounded-full tw-bg-emerald-50/50 hover:tw-bg-emerald-100/50 tw-text-[#25D366] hover:scale-105 tw-transition-all tw-border-none tw-outline-none tw-cursor-pointer"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const leadPhone =
                                            lead?.phoneNo || lead?.mobile || "";
                                          if (!leadPhone) {
                                            message.warning(
                                              "This lead does not have a phone number.",
                                            );
                                            return;
                                          }
                                          const leadName =
                                            `${lead?.firstname || ""} ${lead?.lastname || ""}`.trim() ||
                                            lead?.fullname ||
                                            "Lead";
                                          navigate(
                                            `/whatsapp?leadId=${lead?.id}&phone=${encodeURIComponent(leadPhone)}&name=${encodeURIComponent(leadName)}`,
                                          );
                                        }}
                                      >
                                        <WhatsAppIcon className="tw-size-[14px]" />
                                      </button>
                                    </Tooltip>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="no-data tw-flex tw-items-center tw-justify-center tw-h-full tw-w-full tw-min-h-[250px]">
                        <img
                          src={noData}
                          alt="No data"
                          className="tw-h-32 tw-w-auto tw-object-contain"
                        />
                      </div>
                    )}
                  </div>
                </Col>
              );
            })}
        {!!isDragged && (
          <div className="tw-fixed tw-bottom-6 tw-left-1/2 tw-translate-x-[-50%] tw-w-[90%] tw-max-w-[760px] tw-bg-white/95 tw-backdrop-blur-md tw-border tw-border-solid tw-border-slate-200/80 tw-p-3.5 tw-rounded-2xl tw-z-50 tw-shadow-[0_16px_36px_rgba(15,23,42,0.12)] tw-flex tw-items-center tw-justify-between tw-gap-3.5">
            {/* Delete Zone */}
            <div
              className={`tw-flex-1 tw-py-3.5 tw-px-5 tw-rounded-xl tw-border-2 tw-border-dashed tw-flex tw-items-center tw-justify-center tw-gap-2.5 tw-transition-all tw-duration-200 tw-text-[13px] tw-font-bold tw-tracking-wider tw-uppercase tw-cursor-pointer
                ${
                  status === "delete"
                    ? "tw-bg-rose-600 tw-border-rose-600 tw-text-white tw-scale-[1.02] tw-shadow-[0_8px_20px_rgba(225,29,72,0.25)]"
                    : "tw-bg-rose-50/10 tw-border-rose-200 tw-text-rose-600 hover:tw-bg-rose-50/20"
                }`}
              onDragOver={(e) => {
                setStatus("delete");
                e.preventDefault();
              }}
              onDragEnter={(e) => {
                e.preventDefault();
              }}
              onDragLeave={() => setStatus()}
              onDrop={(e) => {
                e.preventDefault();
                setConfirm(true);
              }}
            >
              <Trash2 size={18} className="tw-shrink-0" />
              <span>Delete</span>
            </div>

            {/* Won Zone */}
            <div
              className={`tw-flex-1 tw-py-3.5 tw-px-5 tw-rounded-xl tw-border-2 tw-border-dashed tw-flex tw-items-center tw-justify-center tw-gap-2.5 tw-transition-all tw-duration-200 tw-text-[13px] tw-font-bold tw-tracking-wider tw-uppercase tw-cursor-pointer
                ${
                  status === "won"
                    ? "tw-bg-emerald-600 tw-border-emerald-600 tw-text-white tw-scale-[1.02] tw-shadow-[0_8px_20px_rgba(16,185,129,0.25)]"
                    : "tw-bg-emerald-50/10 tw-border-emerald-200 tw-text-emerald-600 hover:tw-bg-emerald-50/20"
                }`}
              onDragOver={(e) => {
                setStatus("won");
                e.preventDefault();
              }}
              onDragLeave={() => setStatus()}
              onDrop={(e) => {
                setConfirm(true);
                e.preventDefault();
              }}
            >
              <CheckCircle2 size={18} className="tw-shrink-0" />
              <span>Won</span>
            </div>

            {/* Lost Zone */}
            <div
              className={`tw-flex-1 tw-py-3.5 tw-px-5 tw-rounded-xl tw-border-2 tw-border-dashed tw-flex tw-items-center tw-justify-center tw-gap-2.5 tw-transition-all tw-duration-200 tw-text-[13px] tw-font-bold tw-tracking-wider tw-uppercase tw-cursor-pointer
                ${
                  status === "lost"
                    ? "tw-bg-amber-600 tw-border-amber-600 tw-text-white tw-scale-[1.02] tw-shadow-[0_8px_20px_rgba(245,158,11,0.25)]"
                    : "tw-bg-amber-50/10 tw-border-amber-200 tw-text-amber-600 hover:tw-bg-amber-50/20"
                }`}
              onDragOver={(e) => {
                setStatus("lost");
                e.preventDefault();
              }}
              onDragLeave={() => setStatus()}
              onDrop={(e) => {
                setType("Closed Lost");
                e.preventDefault();
              }}
            >
              <XCircle size={18} className="tw-shrink-0" />
              <span>Lost</span>
            </div>
          </div>
        )}
      </Row>
      <TagLeads
        selectedRecords={selectedRecords}
        showTagModal={showTagModal}
        closeTagModal={() => setShowTagModal(false)}
      />
      <MarkAsLostModal
        type={type}
        setType={setType}
        draggableLead={draggableLead}
        updateLead={(val) => {
          updateLead(val);
          setDragged(false);
        }}
        setStatus={(val) => setStatus(val)}
        handleCancel={() => setDragged(false)}
      />

      {/* Modern Confirmation Modal */}
      <Modal
        open={isConfirm}
        centered
        width={400}
        title={null}
        footer={null}
        closable
        onCancel={handleCancel}
        className="delete-popup"
      >
        <div className="tw-bg-slate-50 tw-text-slate-800 tw-text-sm tw-font-bold tw-py-4 tw-px-6 tw-rounded-t-2xl tw-border tw-border-solid tw-border-slate-100/50 uppercase tw-tracking-wider">
          {status === "won" ? "Confirm Lead Won" : "Confirm Delete Lead"}
        </div>
        <div className="tw-p-6">
          <div className="tw-text-sm tw-font-medium tw-text-slate-600 tw-mb-6">
            {status === "won"
              ? "Are you sure you want to mark this lead as won?"
              : "Are you sure you want to delete this lead? This action cannot be undone."}
          </div>
          <div className="tw-flex tw-items-center tw-justify-end tw-gap-3">
            <Button
              className="!tw-h-[38px] !tw-rounded-lg !tw-border-slate-200 !tw-bg-white !tw-px-5 !tw-text-xs !tw-font-bold !tw-text-slate-500 hover:!tw-border-slate-300 hover:!tw-text-slate-700 tw-transition-all tw-duration-200"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              className={
                status === "won"
                  ? "!tw-bg-theme !tw-border-theme !tw-text-white !tw-h-[38px] !tw-rounded-lg !tw-px-5 !tw-text-xs !tw-font-bold tw-shadow-sm hover:tw-shadow-md tw-transition-all"
                  : "!tw-bg-rose-600 !tw-border-rose-600 !tw-text-white !tw-h-[38px] !tw-rounded-lg !tw-px-5 !tw-text-xs !tw-font-bold tw-shadow-sm hover:tw-shadow-md tw-transition-all"
              }
              onClick={() => {
                updateLead({
                  ...draggableLead,
                  status: status === "won" ? 16 : 19,
                });
                handleCancel();
              }}
            >
              {status === "won" ? "Mark as won" : "Delete"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default KanbanView;
